import { useEffect, useState } from "react";
import { MenuQrCard, EventsQrCard } from "@/components/admin/MenuQrCard";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { adminApi, ASSETS, formatApiError } from "@/lib/api";
import {
  LayoutDashboard,
  CalendarDays,
  Sparkles,
  Users,
  Briefcase,
  LogOut,
  Settings,
  UserCog,
  Star,
  KeyRound,
  Mail,
  Image as ImageIcon,
  Menu as MenuIcon,
  X,
  CalendarClock,
  DollarSign,
  UtensilsCrossed,
  Swords,
  ScanLine,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import EventsManager from "@/components/admin/EventsManager";
import SpecialsManager from "@/components/admin/SpecialsManager";
import ReservationsList from "@/components/admin/ReservationsList";
import ApplicationsList from "@/components/admin/ApplicationsList";
import UsersManager from "@/components/admin/UsersManager";
import SiteContentManager from "@/components/admin/SiteContentManager";
import ReviewsManager from "@/components/admin/ReviewsManager";
import EmailsManager from "@/components/admin/EmailsManager";
import GalleryManager from "@/components/admin/GalleryManager";
import ScheduleManager from "@/components/admin/ScheduleManager";
import CommissionsManager from "@/components/admin/CommissionsManager";
import ContestsManager from "@/components/admin/ContestsManager";
import { DoorSummary } from "@/components/DoorSummary";
import StaffReviewsManager from "@/components/admin/StaffReviewsManager";
import MenusManager from "@/components/admin/MenusManager";

const ALL_TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard, perm: null },
  { id: "events", label: "Events", icon: CalendarDays, perm: "events" },
  { id: "specials", label: "Specials", icon: Sparkles, perm: "specials" },
  { id: "reservations", label: "Reservations", icon: Users, perm: "reservations" },
  { id: "applications", label: "Applications", icon: Briefcase, perm: "applications" },
  { id: "reviews", label: "Reviews", icon: Star, perm: "reviews" },
  { id: "gallery", label: "Gallery", icon: ImageIcon, perm: "gallery" },
  { id: "schedule", label: "Schedule", icon: CalendarClock, perm: "schedule" },
  { id: "commissions", label: "Commissions", icon: DollarSign, perm: "schedule" },
  { id: "contests", label: "Contests", icon: Swords, perm: "schedule" },
  { id: "crew-reviews", label: "Crew Reviews", icon: Star, perm: "schedule" },
  { id: "menus", label: "Menus", icon: UtensilsCrossed, perm: "content" },
  { id: "content", label: "Site Content", icon: Settings, perm: "content" },
  { id: "emails", label: "Emails", icon: Mail, perm: "__owner__" },
  { id: "users", label: "Team", icon: UserCog, perm: "__owner__" },
];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const perms = user?.effective_permissions || [];
  const isOwner = user?.role === "owner";
  const tabs = ALL_TABS.filter((t) =>
    !t.perm ? true : t.perm === "__owner__" ? isOwner : perms.includes(t.perm)
  );

  useEffect(() => {
    if (tab === "overview" && user) {
      adminApi.get("/admin/summary").then((r) => setSummary(r.data)).catch(() => {});
    }
  }, [tab, user]);

  useEffect(() => {
    // If current tab isn't visible for this user, jump to overview
    if (user && !tabs.find((t) => t.id === tab)) setTab("overview");
  }, [user, tab, tabs]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role === "staff") return <Navigate to="/staff" replace />;

  function handleLogout() {
    logout();
    navigate("/admin/login");
  }

  function selectTab(id) {
    setTab(id);
    setSidebarOpen(false); // close drawer on mobile after picking
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen md:flex bg-[var(--trio-bg)]">
      {/* Mobile top bar with hamburger */}
      <div className="md:hidden sticky top-0 z-40 flex items-center justify-between bg-[var(--trio-text)] text-[var(--trio-bg)] px-4 py-3 border-b border-[var(--trio-bg)]/15" style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)" }}>
        <button
          onClick={() => setSidebarOpen(true)}
          data-testid="admin-sidebar-open"
          aria-label="Open menu"
          className="p-2 -ml-2"
        >
          <MenuIcon className="w-6 h-6" />
        </button>
        <img src={ASSETS.logo} alt="Trio" className="h-7 w-auto invert" />
        <div className="w-8" />
      </div>

      {/* Sidebar backdrop on mobile */}
      {sidebarOpen && (
        <button
          aria-label="Close menu"
          onClick={() => setSidebarOpen(false)}
          data-testid="admin-sidebar-backdrop"
          className="md:hidden fixed inset-0 z-40 bg-black/60"
        />
      )}

      <aside
        data-testid="admin-sidebar"
        className={`fixed md:static inset-y-0 left-0 z-50 w-72 md:w-60 shrink-0 bg-[var(--trio-text)] text-[var(--trio-bg)] flex flex-col transition-transform duration-200 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="p-6 border-b border-[var(--trio-bg)]/15 flex items-start justify-between">
          <div>
            <img src={ASSETS.logo} alt="Trio" className="h-10 w-auto invert" />
            <div className="mt-2 text-[10px] tracking-[0.3em] uppercase text-[var(--trio-accent)]">
              {isOwner ? "Owner" : "Admin"}
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            data-testid="admin-sidebar-close"
            aria-label="Close menu"
            className="md:hidden p-1 -mr-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 p-3 overflow-y-auto">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => selectTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm tracking-wide uppercase transition-colors ${
                  active
                    ? "bg-[var(--trio-primary)] text-white"
                    : "hover:bg-[var(--trio-bg)]/10"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
          <Link
            to="/door"
            data-testid="admin-door-scanner"
            className="mt-3 flex items-center gap-3 px-3 py-2.5 text-sm bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white uppercase tracking-widest text-xs font-bold"
          >
            <ScanLine className="w-4 h-4" /> Door Scanner
          </Link>
        </nav>
        <div className="p-4 border-t border-[var(--trio-bg)]/15 space-y-2">
          <div className="text-xs text-[var(--trio-bg)]/60 mb-1">{user.name}</div>
          <div className="text-[10px] text-[var(--trio-bg)]/40 mb-3 truncate">
            {user.email}
          </div>
          <ChangePasswordButton />
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="w-full inline-flex items-center justify-center gap-2 border border-[var(--trio-bg)]/30 hover:bg-[var(--trio-bg)] hover:text-[var(--trio-text)] py-2 text-xs tracking-widest uppercase transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 md:overflow-y-auto min-w-0">
        <header className="border-b border-[var(--trio-border)] px-4 md:px-8 py-5 bg-[var(--trio-bg)] sticky top-[52px] md:top-0 z-10">
          <h1
            data-testid="admin-page-title"
            className="font-display uppercase text-2xl md:text-3xl tracking-tight"
          >
            {tabs.find((t) => t.id === tab)?.label || "Dashboard"}
          </h1>
        </header>

        <div className="p-4 md:p-8">
          {tab === "overview" && <Overview summary={summary} setTab={selectTab} tabs={tabs} />}
          {tab === "events" && <EventsManager />}
          {tab === "specials" && <SpecialsManager />}
          {tab === "reservations" && <ReservationsList />}
          {tab === "applications" && <ApplicationsList />}
          {tab === "reviews" && <ReviewsManager />}
          {tab === "gallery" && <GalleryManager />}
          {tab === "schedule" && <ScheduleManager />}
          {tab === "commissions" && <CommissionsManager />}
          {tab === "contests" && <ContestsManager />}
          {tab === "crew-reviews" && <StaffReviewsManager />}
          {tab === "menus" && <MenusManager />}
          {tab === "content" && <SiteContentManager />}
          {tab === "emails" && <EmailsManager />}
          {tab === "users" && <UsersManager />}
        </div>
      </main>
    </div>
  );
}

function ChangePasswordButton() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be 6+ characters.");
    if (next !== confirm) return toast.error("New passwords don't match.");
    setSaving(true);
    try {
      await adminApi.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated.");
      setOpen(false);
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="admin-change-password-btn"
        className="w-full inline-flex items-center justify-center gap-2 border border-[var(--trio-bg)]/30 hover:bg-[var(--trio-bg)] hover:text-[var(--trio-text)] py-2 text-xs tracking-widest uppercase transition-colors"
      >
        <KeyRound className="w-3.5 h-3.5" /> Change Password
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="change-password-dialog"
          className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight">
              Change your password
            </DialogTitle>
            <DialogDescription className="text-sm text-[var(--trio-muted)]">
              6 characters minimum. Signs you back in immediately.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs tracking-widest uppercase">Current password</Label>
              <Input
                type="password"
                required
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                data-testid="cp-current"
                className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)]"
              />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase">New password</Label>
              <Input
                type="password"
                required
                minLength={6}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                data-testid="cp-new"
                className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)]"
              />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase">Confirm new password</Label>
              <Input
                type="password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="cp-confirm"
                className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)]"
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="cp-submit"
              className="w-full bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white py-3 text-xs tracking-widest uppercase font-bold disabled:opacity-60 transition-colors"
            >
              {saving ? "Saving…" : "Update Password"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Overview({ summary, setTab, tabs }) {
  const visible = new Set(tabs.map((t) => t.id));
  const all = [
    {
      label: "Reservations",
      value: summary?.reservations?.total ?? "—",
      sub: `${summary?.reservations?.pending ?? 0} pending`,
      tab: "reservations",
    },
    {
      label: "Event RSVPs",
      value: summary?.reservations?.event ?? "—",
      sub: `${summary?.reservations?.group ?? 0} group parties`,
      tab: "reservations",
    },
    {
      label: "Applications",
      value: summary?.applications?.total ?? "—",
      sub: `${summary?.applications?.new ?? 0} new`,
      tab: "applications",
    },
    {
      label: "Active Events",
      value: summary?.events_active ?? "—",
      sub: "Live on homepage",
      tab: "events",
    },
    {
      label: "Active Specials",
      value: summary?.specials_active ?? "—",
      sub: "Shown to guests",
      tab: "specials",
    },
    {
      label: "App Installs",
      value: summary?.app_installs ?? "—",
      sub: "Discount codes issued",
    },
  ];
  const cards = all.filter((c) => !c.tab || visible.has(c.tab));
  return (
    <div data-testid="admin-overview" className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <button
            key={c.label}
            onClick={() => c.tab && visible.has(c.tab) && setTab(c.tab)}
            data-testid={`overview-card-${c.label.toLowerCase().replace(/ /g, "-")}`}
            className="text-left bg-[var(--trio-surface)] border border-[var(--trio-border)] hover:border-[var(--trio-primary)] p-6 transition-colors"
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold mb-3">
              {c.label}
            </div>
            <div className="font-display text-5xl tracking-tight">{c.value}</div>
            <div className="mt-2 text-xs text-[var(--trio-muted)]">{c.sub}</div>
          </button>
        ))}
      </div>
      <div className="bg-[var(--trio-surface)] border border-[var(--trio-border)] p-8">
        <h3 className="font-display uppercase text-2xl tracking-tight mb-3">
          Welcome back.
        </h3>
        <p className="text-sm text-[var(--trio-muted)] leading-relaxed max-w-2xl">
          Everything you post goes live on the homepage instantly. Use{" "}
          <em>Site Content</em> to tweak the wording, hours, and marquee.
        </p>
      </div>
      <DoorSummary />
      <EventsQrCard />
      <MenuQrCard />
    </div>
  );
}
